import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { ErrorService } from './error.service';

/**
 * Endpoints whose own 401 must never trigger a refresh.
 *
 * `/auth/refresh` for the obvious reason — refreshing a failed refresh is an
 * infinite loop. The sign-in paths because a wrong password answers 401 too,
 * and retrying it would swallow the error the form needs to show while
 * spending the refresh token for nothing.
 */
const NEVER_REFRESH = ['/auth/refresh', '/auth/login', '/auth/azure-callback', '/auth/password-reset'];

const isRefreshable = (req: HttpRequest<unknown>) =>
  !NEVER_REFRESH.some(p => req.url.includes(p));

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const auth = inject(AuthService);
  const errors = inject(ErrorService);

  return next(req).pipe(
    catchError((err: HttpErrorResponse): Observable<unknown> => {
      if (err.status === 401 && isRefreshable(req)) {
        // An expired access token is not the end of a session — it is the
        // fifteen-minute mark. Before this, it signed the user out mid-wizard
        // and lost whatever they had typed.
        return auth.refreshSession().pipe(
          switchMap(token => {
            if (!token) {
              auth.logout();
              router.navigate(['/login']);
              return throwError(() => err);
            }
            return next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }));
          })
        );
      }

      if (err.status === 401) {
        auth.logout();
        router.navigate(['/login']);
      } else if (err.status === 403) {
        errors.show('You do not have permission for that action.');
      } else if (err.status >= 500) {
        errors.show('Something went wrong on the server.');
      }
      return throwError(() => err);
    })
  ) as Observable<never>;
};
